'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';

interface BriefingStory {
  title: string;
  slug: { current: string };
  categories?: { title: string; slug: { current: string } }[];
}

interface DailyBriefingBandProps {
  stories: BriefingStory[];
}

function formatDate() {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).toUpperCase();
}

function formatTime() {
  return new Date().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

export default function DailyBriefingBand({ stories }: DailyBriefingBandProps) {
  const [time, setTime] = useState('');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setTime(formatTime());
    const interval = setInterval(() => setTime(formatTime()), 60000);
    return () => clearInterval(interval);
  }, []);

  if (!stories || stories.length === 0) return null;

  const top10 = stories.slice(0, 10);

  return (
    <div className="w-full relative border-b border-border/60 bg-background/95 backdrop-blur-2xl z-40 shadow-sm">
      {/* 0.1% Premium Edge Glow */}
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-brand to-transparent opacity-50" />

      <div className="px-4 md:px-6 lg:px-8 py-4 lg:py-5">
        <div className="flex flex-col lg:flex-row lg:items-stretch gap-6 lg:gap-8">
          
          {/* LEFT PANEL: Branding & Global Time */}
          <div className="flex-shrink-0 flex items-center justify-between lg:flex-col lg:items-start lg:justify-center lg:w-[180px] xl:w-[220px] lg:border-r lg:border-border/40 lg:pr-6">
            <div className="flex items-center gap-2.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-brand"></span>
              </span>
              <span className="text-[11px] font-black uppercase tracking-[0.3em] text-foreground/90">
                Daily Briefing
              </span>
            </div>
            
            <div className="hidden sm:flex lg:flex-col items-center lg:items-start gap-4 lg:gap-1.5 mt-0 lg:mt-4">
              <p className="text-[10px] text-muted-foreground font-mono tracking-[0.1em] uppercase">
                {formatDate()}
              </p>
              {mounted && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-brand font-mono font-bold tracking-[0.1em]">
                    {time}
                  </span>
                  <span className="text-[8px] text-muted-foreground/60 font-mono border border-border/50 px-1 rounded-sm">LIVE</span>
                </div>
              )}
            </div>
          </div>

          {/* CENTER PANEL: High-Density Intelligence Grid (Linear / Bloomberg Style) */}
          <div className="flex-1 w-full overflow-hidden flex items-center">
            {/* Mobile/Tablet: Horizontal Swipe. Desktop: 5-column grid */}
            <div className="flex overflow-x-auto lg:grid lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-x-8 lg:gap-y-6 pb-4 lg:pb-0 snap-x snap-mandatory hide-scrollbar w-full">
              {top10.map((story, i) => {
                const cat = story.categories?.[0];
                return (
                  <Link
                    key={i}
                    href={`/post/${story.slug.current}`}
                    className="group relative flex flex-col min-w-[280px] sm:min-w-[320px] lg:min-w-0 snap-start p-4 lg:p-0 rounded-xl lg:rounded-none bg-muted/30 lg:bg-transparent border border-border/40 lg:border-none hover:bg-muted/80 lg:hover:bg-transparent transition-all duration-300"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] font-bold text-brand font-mono bg-brand/10 px-1.5 py-0.5 rounded-sm">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      {cat && (
                        <>
                          <span className="w-1 h-1 rounded-full bg-border group-hover:bg-brand/50 transition-colors" />
                          <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-muted-foreground group-hover:text-foreground transition-colors line-clamp-1">
                            {cat.title}
                          </span>
                        </>
                      )}
                    </div>
                    <h4 className="text-[13px] sm:text-[14px] font-serif font-bold leading-tight text-foreground group-hover:text-brand transition-colors line-clamp-2">
                      {story.title}
                    </h4>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* RIGHT PANEL: Archives Button */}
          <div className="hidden lg:flex flex-shrink-0 items-center justify-center pl-6 border-l border-border/40">
            <Link
              href="/archive"
              className="group relative inline-flex items-center justify-center h-10 px-5 text-[10px] font-black uppercase tracking-[0.2em] text-foreground bg-muted/20 border border-border/50 hover:border-brand hover:bg-brand/5 transition-all duration-300 rounded-md overflow-hidden"
            >
              <span className="relative z-10 flex items-center gap-2">
                Full Archives
                <svg className="w-3.5 h-3.5 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
            </Link>
          </div>

        </div>
      </div>
      
      {/* Utility to hide scrollbar for horizontal swipe on mobile */}
      <style jsx global>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
